import React from 'react';
import {Tooltip, OverlayTrigger} from 'react-bootstrap';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {IWifiAnalyzerHistoryController} from './controller';
import {observer, inject, IReactComponent} from 'mobx-react';
import {IWifiAnalyzerHistoryStore} from './wifi-analyzer-history.stores';
import {IAppRootStore} from '../../app.root.store';
import {SimpleLabel} from '@techsee/techsee-ui-common/lib/forms/labels/simple';
import {SecondaryButton} from '@techsee/techsee-ui-common/lib/forms/buttons/secondary';
import {Table} from '@techsee/techsee-ui-common/lib/table';
import {Icon} from '@techsee/techsee-ui-common/lib/icons/simple-icon';
import {WifiAnalyzer, WifiAnalyzerMode} from '../../components/wifi-analyzer/component';
import {
    Spinner,
    SpinnerBackgroundColors,
    SpinnerColors,
    SpinnerSizes,
    SpinnerTypeOptions
} from '@techsee/techsee-ui-common/lib/spinner';
import {isEmpty} from 'lodash';
import {ForwardBirthCertificateReportModal} from './forward-birth-certificate-report-modal/view';

import '@techsee/techsee-ui-common/lib/_shared/icons.css';
import './style.scss';
import {PollingBirthCertificateReport} from './polling-birth-certificate-report/view';
import {PollingBirthCertificateReportController} from './polling-birth-certificate-report/controller';
import {GuruReportsTypes} from '@techsee/techsee-common/lib/constants/guru-reports.constants';
import {ScreenLoader} from '../../components/screen-loader/screen-loader';

export interface IWifiAnalyzerViewProps {
    wifiAnalyzerHistoryStore: IWifiAnalyzerHistoryStore;
    rootStore: IAppRootStore;
}

@observer
export class WifiAnalyzerViewComponent extends TechseeBaseComponent<IWifiAnalyzerViewProps> {
    get controller(): IWifiAnalyzerHistoryController {
        return this.props.wifiAnalyzerHistoryStore?.wifiAnalyzerHistoryController;
    }

    renderScanId = (session: any) => {
        const controller = this.controller;
        const {preDataCollectionCode} = controller;

        if (preDataCollectionCode || !session?.sessionSource) {
            return (
                <span className='scan-type-link' onClick={() => controller.goToDashboard(session.roomId, session._id)}>
                    {session._id}
                </span>
            );
        }

        return <span>{session._id}</span>;
    };

    renderScanTypeLinks = (session: any) => {
        const controller = this.controller;
        const {formatScanTypeLabels, is3dHeatMapEnabled, selectSession, preDataCollectionCode} = controller;
        const {scanTypeLabel} = formatScanTypeLabels(session);

        return (
            <>
                {is3dHeatMapEnabled(session) && (
                    <span
                        className='scan-type-link'
                        onClick={(e) => {
                            controller.open3dHeatMap(e, session);
                        }}>
                        {/* TODO: refactor scanTypeLabel3DHeatMap */}
                        3D Heatmap
                    </span>
                )}

                {scanTypeLabel && is3dHeatMapEnabled(session) && <span className='scan-type-separator' />}

                {scanTypeLabel && (
                    <span
                        className='scan-type-link'
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            selectSession(session);
                        }}>
                        {scanTypeLabel}
                    </span>
                )}
            </>
        );
    };

    getOsIcon = (platformType: string): JSX.Element =>
        platformType && platformType.toLowerCase().includes('android') ? (
            <Icon className='os-icon' iconName='android-rounded' />
        ) : (
            <Icon className='os-icon' iconName='ios' />
        );

    getFloorFormatted = (session: any): React.ReactNode => {
        if (session.scanDetails && typeof session.scanDetails.floor !== 'undefined') {
            return session.scanDetails.floor.toString();
        }
        return '';
    };

    getRoomFormatted = (session: any): React.ReactNode => {
        if (
            session.scanDetails?.roomType &&
            session.scanDetails.roomType !== '-' &&
            session.scanDetails.roomType !== 'N/A'
        ) {
            return session.scanDetails.roomType;
        }
        return '';
    };

    getNotesFormatted = (session: any): React.ReactNode => {
        let noteText: string | undefined;

        if (session.scanDetails && session.scanDetails.note) {
            noteText = session.scanDetails.note;
        } else if (session.description) {
            noteText = session.description;
        }

        if (!noteText || noteText === 'N/A') {
            return '';
        }

        return (
            <OverlayTrigger
                placement='top'
                overlay={
                    <Tooltip id={`tooltip-note-${session._id}`} className='tooltip-wifi-history'>
                        {noteText}
                    </Tooltip>
                }
                delay={200}>
                <div className='note-cell'>{noteText}</div>
            </OverlayTrigger>
        );
    };

    renderInternal(): JSX.Element {
        const controller = this.controller;

        if (!controller) {
            return <ScreenLoader></ScreenLoader>;
        }

        const {
            translate,
            goToInvitePage,
            prevPage,
            nextPage,
            currentPage,
            totalPages,
            isNextAvailable,
            isPrevAvailable,
            wifiAnalyzerList,
            startDate,
            endDate,
            getDateFormattedByAcc,
            wifiAnalyzerController,
            forwardBirthCertificateReportModalController,
            is3dHeatMapEnabled
        } = controller;

        return (
            <div className='vj-history-state use-common-styles dashboard-main-container'>
                <div className='vj-history-head'>
                    {goToInvitePage && (
                        <div className='go-back' onClick={goToInvitePage}>
                            <Icon className='go-back-icon' iconName={'arrow-back'} />
                            <div className='go-back-text'>{translate('REACT.INTERACTION.SUMMARY.VJ.LIST.GO.BACK')}</div>
                        </div>
                    )}
                    <SimpleLabel className='title'>{translate('REACT.WIFI_ANALYZER.HISTORY_LIST_TITLE')}</SimpleLabel>
                    {startDate && endDate && (
                        <SimpleLabel className='subtitle'>
                            {translate('REACT.INTERACTION.SUMMARY.VJ.LIST.FROM_TILL', {
                                from: getDateFormattedByAcc(startDate, true),
                                till: getDateFormattedByAcc(endDate, true)
                            })}
                        </SimpleLabel>
                    )}
                </div>
                <div className='vj-history-table'>
                    <Table bordered condensed hover>
                        <thead>
                            <tr className='table-head'>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_DATE')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_USERNAME')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_CUSTOMER_ID')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_SESSION_ID')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_SCAN_ID')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_FLOOR')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_ROOM')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_NOTES')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_DEVICE')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.HISTORY_SCAN_TYPE')}</th>
                                <th>{translate('REACT.WIFI_ANALYZER.ACTIONS')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {wifiAnalyzerList.map((session: any, index: number) => {
                                return (
                                    <tr key={index}>
                                        <td>{getDateFormattedByAcc(session.createdOn, true)}</td>
                                        <td>{session.userName}</td>
                                        <td>{session.customerId}</td>
                                        <td>{session.roomId}</td>
                                        <td className='scan-type-cell'>{this.renderScanId(session)}</td>
                                        <td>{this.getFloorFormatted(session)}</td>
                                        <td>{this.getRoomFormatted(session)}</td>
                                        <td>{this.getNotesFormatted(session)}</td>
                                        <td>
                                            <div className='device-cell'>
                                                {this.getOsIcon(session.platformType)}
                                                <span>{session.deviceModel}</span>
                                            </div>
                                        </td>
                                        <td className='scan-type-cell'>{this.renderScanTypeLinks(session)}</td>
                                        <td>
                                            <div className='action-icons-container'>
                                                <PollingBirthCertificateReport
                                                    ctrl={new PollingBirthCertificateReportController(translate)}
                                                    session={session}
                                                    reportType={GuruReportsTypes.CUSTOMER}
                                                    reportConfig={{
                                                        iconName: 'email-open',
                                                        popupTitle: translate('REACT.WIFI_ANALYZER.CUSTOMER_REPORT'),
                                                        generateTooltip: translate(
                                                            'REACT.WIFI_ANALYZER.GENERATE_CUSTOMER_REPORT'
                                                        ),
                                                        viewTooltip: translate(
                                                            'REACT.WIFI_ANALYZER.VIEW_CUSTOMER_REPORT'
                                                        ),
                                                        errorMessage: translate(
                                                            'REACT.WIFI_ANALYZER.GENERATE_REPORT_ERROR'
                                                        )
                                                    }}
                                                    onForwardReport={(scanId, reportType) => {
                                                        controller.openForwardBirthCertificateReportModal(
                                                            new MouseEvent('click') as any,
                                                            scanId,
                                                            reportType
                                                        );
                                                    }}
                                                    disabled={!is3dHeatMapEnabled(session)}
                                                />
                                                <PollingBirthCertificateReport
                                                    ctrl={new PollingBirthCertificateReportController(translate)}
                                                    session={session}
                                                    reportType={GuruReportsTypes.TECHNICIAN}
                                                    reportConfig={{
                                                        iconName: 'briefcase',
                                                        popupTitle: translate('REACT.WIFI_ANALYZER.TECHNICIAN_REPORT'),
                                                        generateTooltip: translate(
                                                            'REACT.WIFI_ANALYZER.GENERATE_TECHNICIAN_REPORT'
                                                        ),
                                                        viewTooltip: translate(
                                                            'REACT.WIFI_ANALYZER.VIEW_TECHNICIAN_REPORT'
                                                        ),
                                                        errorMessage: translate(
                                                            'REACT.WIFI_ANALYZER.GENERATE_REPORT_ERROR'
                                                        )
                                                    }}
                                                    onForwardReport={(scanId, reportType) => {
                                                        controller.openForwardBirthCertificateReportModal(
                                                            new MouseEvent('click') as any,
                                                            scanId,
                                                            reportType
                                                        );
                                                    }}
                                                    disabled={!is3dHeatMapEnabled(session)}
                                                />
                                                <PollingBirthCertificateReport
                                                    ctrl={new PollingBirthCertificateReportController(translate)}
                                                    session={session}
                                                    reportType={GuruReportsTypes.AGGREGATED}
                                                    reportConfig={{
                                                        iconName: 'paper',
                                                        popupTitle: translate('REACT.WIFI_ANALYZER.AGGREGATED_REPORT'),
                                                        generateTooltip: translate(
                                                            'REACT.WIFI_ANALYZER.AGGREGATED_REPORT_OPTIONS'
                                                        ),
                                                        viewTooltip: translate(
                                                            'REACT.WIFI_ANALYZER.VIEW_AGGREGATED_REPORT'
                                                        )
                                                    }}
                                                    onForwardReport={(roomId) => {
                                                        controller.openForwardAggregatedReportModal(
                                                            new MouseEvent('click') as any,
                                                            roomId
                                                        );
                                                    }}
                                                    disabled={!session.aggregatedBirthCertificateReportUrl}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                    {isEmpty(wifiAnalyzerList) && (
                        <div className='wifi-spinner-wrapper'>
                            <Spinner
                                spinnerType={SpinnerTypeOptions.CIRCULAR}
                                sizeName={SpinnerSizes.Large}
                                backgroundColorName={SpinnerBackgroundColors.Transparent}
                                colorName={SpinnerColors.LightBlue}
                            />
                        </div>
                    )}
                </div>
                <div className='vj-history-table-pagination'>
                    <div className='prev-button'>
                        <SecondaryButton onClick={prevPage} disabled={!isPrevAvailable}>
                            <Icon iconName={'arrow-back'} />
                            {translate('REACT.INTERACTION.SUMMARY.VJ.LIST.PREVIOUS')}
                        </SecondaryButton>
                    </div>
                    <div className='page-of-pages'>
                        <SimpleLabel>
                            {translate('REACT.INTERACTION.SUMMARY.VJ.LIST.PAGE_OF_PAGES', {
                                page: currentPage,
                                pages: totalPages
                            })}
                        </SimpleLabel>
                    </div>
                    <div className='next-button'>
                        <SecondaryButton onClick={nextPage} disabled={!isNextAvailable}>
                            {translate('REACT.INTERACTION.SUMMARY.VJ.LIST.NEXT')}
                            <Icon iconName={'arrow-next'} />
                        </SecondaryButton>
                    </div>
                </div>
                <WifiAnalyzer ctrl={wifiAnalyzerController} mode={WifiAnalyzerMode.History} />
                <ForwardBirthCertificateReportModal ctrl={forwardBirthCertificateReportModalController} />
            </div>
        );
    }
}

export const WifiAnalyzerView = inject(
    'rootStore',
    'wifiAnalyzerHistoryStore'
)(WifiAnalyzerViewComponent) as IReactComponent;
